import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { useStoreSelection } from '@/context/StoreSelectionContext'
import { useAddressAutocomplete } from './hooks/useAddressAutocomplete'
import { useGetFullAddressDetails } from './hooks/useAddressDetails'
import { useAddressTransform } from './hooks/useAddressTransform'
import { useGeolocation } from '@/hooks/useGeolocation'
import { ScreenHeader } from '@/features/AddressSearchScreen/Components/ScreenHeader'
import { AddressInput } from '@/features/AddressSearchScreen/Components/AddressInput'
import { SavedAddresses } from '@/features/AddressSearchScreen/Components/SavedAddresses'
import { useAuthGuestMode, AuthLoadingOverlay } from '@/features/Authentication'
import { LocationMarkerIcon, SpinnerIcon, AlertCircleIcon, MapPinIcon, UserIcon } from '@/components/ui/icons'
import { selectIsAuthenticated, selectAppUser } from '@/features/Authentication/authSlice'

interface AddressOption {
  label: string
  value: string
  description?: string
  placeId?: string
  isPostcodeData?: boolean
  postcodeData?: any
  structured_formatting?: any
}

export function AddressSearchScreen() {
  const navigate = useNavigate()
  const { state } = useStoreSelection()
  const isAuthenticated = useSelector(selectIsAuthenticated)
  const appUser = useSelector(selectAppUser)
  const [addressSearchQuery, setAddressSearchQuery] = useState('')
  // const [showManualEntry, setShowManualEntry] = useState(false) // Commented out - manual entry disabled
  const [isSelectingAddress, setIsSelectingAddress] = useState(false)
  const { requestLocation, loading: locationLoading, error: locationError } = useGeolocation()
  const { guestMode, handleGuestContinue } = useAuthGuestMode()
  const { suggestions, isLoading: loading, error: queryError } = useAddressAutocomplete(addressSearchQuery)
  const { 
    handleSelect: handleAddressSelect, 
    isLoadingAddress, 
    fullAddressDetails,
    error: addressError 
  } = useGetFullAddressDetails()
  const deliveryAddress = useAddressTransform(fullAddressDetails)

  useEffect(() => {
    if (deliveryAddress) {
      navigate('/delivery-results', { state: { deliveryAddress } })
      setIsSelectingAddress(false)
    }
  }, [deliveryAddress, navigate])

  // Only show address error if we're actively selecting an address
  const error = queryError ? 'Failed to fetch address suggestions' : (addressError && isSelectingAddress) ? 'Failed to select address. Please try again.' : null

  const handleSelectAddress = async (option: AddressOption) => {
    setIsSelectingAddress(true)
    await handleAddressSelect(option)
  }

  const clearSearch = () => {
    setAddressSearchQuery('')
  }

  const handleUseCurrentLocation = async () => {
    const location = await requestLocation()
    console.log("location", location)
    if (location) {
      navigate('/confirm-location', { state: { lat: location.latitude, lng: location.longitude } })
    }
  }


  // Show validation hint when query is too short
  const showMinCharHint = addressSearchQuery.length > 0 && addressSearchQuery.length < 3 && !guestMode && !isAuthenticated

  const showSuggestions = addressSearchQuery.length >= 3 && suggestions.length > 0
  const showLoginPrompt = addressSearchQuery.length < 3 && !isAuthenticated && !guestMode
  const showSearchPrompt = (addressSearchQuery.length < 3 && guestMode) || (addressSearchQuery.length < 3 && isAuthenticated && state.savedAddresses.length === 0)
  const showWelcomeWithSavedAddressButton = addressSearchQuery.length < 3 && isAuthenticated

  return (
    <div className="min-h-dvh flex flex-col bg-surface-secondary">
      <ScreenHeader
        title="Delivery Address"
        showBack
        onBack={() => navigate('/')}
      />

      {/* Search Input */}
      <div className="px-4 py-4">
        <AddressInput
          value={addressSearchQuery}
          onChange={setAddressSearchQuery}
          autoFocus={isAuthenticated}
          loading={loading}
          onClear={clearSearch}
        />
      </div>

      {/* Use Current Location */}
      <div className="px-4">
        <button
          onClick={handleUseCurrentLocation}
          disabled={locationLoading}
          className="
            w-full py-3 flex items-center gap-3
            text-left
            hover:bg-surface-tertiary
            rounded-xl
            transition-colors
            focus-visible:outline-none focus-visible:bg-surface-tertiary
            disabled:opacity-50
          "
        >
          <div className="size-10 flex items-center justify-center rounded-lg bg-sky-100 dark:bg-sky-900/30 text-sky-600 dark:text-sky-400">
            {locationLoading ? (
              <SpinnerIcon className="size-5" />
            ) : (
              <LocationMarkerIcon className="size-5" />
            )}
          </div>
          <div className="flex-1">
            <span className="font-medium text-text-primary">
              {locationLoading ? 'Getting location...' : 'Use current location'}
            </span>
            {locationError && (
              <p className="text-xs text-status-error">{locationError}</p>
            )}
          </div>
        </button>
      </div>

      {/* Validation hint */}
      {showMinCharHint && (
        <div className="px-4 py-2 flex items-center gap-2 text-sm text-text-tertiary">
          <AlertCircleIcon className="size-4" />
          <span>Enter at least 3 characters to search</span>
        </div>
      )}

      {/* API Error */}
      {error && (
        <div className="mx-4 mt-2 p-3 bg-status-error-light border border-status-error/20 rounded-lg">
          <p className="text-sm text-status-error">{error}</p>
        </div>
      )}

      <div className="h-px bg-border-light mx-4 my-2" />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        {showSuggestions ? (
          <div className="divide-y divide-border-light">
            {suggestions.map((option, index) => (
              <button
                key={option.value || index}
                onClick={() => handleSelectAddress(option)}
                disabled={isLoadingAddress}
                className="
                  w-full px-4 py-3 flex items-center gap-3
                  text-left
                  hover:bg-surface-tertiary
                  transition-colors
                  focus-visible:outline-none focus-visible:bg-surface-tertiary
                  disabled:opacity-50 disabled:cursor-not-allowed
                "
              >
                <div className="size-10 flex items-center justify-center rounded-lg bg-surface-tertiary text-text-secondary">
                  <MapPinIcon className="size-5" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-text-primary truncate">
                    {option.label}
                  </p>
                  {option.description && (
                    <p className="text-sm/5 text-text-secondary truncate">
                      {option.description}
                    </p>
                  )}
                </div>
              </button>
            ))}

            {/* Manual Entry Link - COMMENTED OUT */}
            {/* <div className="px-4 py-4">
              <button
                onClick={() => setShowManualEntry(true)}
                className="text-sm/5 text-brand-primary font-medium hover:underline flex items-center gap-1"
              >
                <PencilIcon className="size-4" />
                Can't find your address? Enter it manually →
              </button>
            </div> */}
          </div>
        ) : /* showManualEntry ? (
          <ManualAddressEntry
            onSubmit={(address) => {
              navigate('/delivery-results', { state: { deliveryAddress: address } })
            }}
            onCancel={() => setShowManualEntry(false)}
          />
        ) : */ showLoginPrompt ? (
          <div className="px-4 py-8">
            <div className="max-w-md mx-auto text-center">
              <div className="size-16 mx-auto mb-4 flex items-center justify-center rounded-full bg-brand-primary/10">
                <UserIcon className="size-8 text-brand-primary" />
              </div>
              <h3 className="text-lg font-semibold text-text-primary mb-2">
                Log in to continue
              </h3>
              <p className="text-sm text-text-secondary mb-6">
                Access your saved addresses and order history
              </p>
              <button
                onClick={() => navigate('/login', { state: { returnTo: '/address-search' } })}
                className="
                  w-full h-13 px-6
                  bg-brand-primary text-white
                  rounded-xl font-semibold
                  hover:opacity-90
                  active:scale-[0.98]
                  transition-all duration-200
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                  flex items-center justify-center gap-2
                "
              >
                Log In
              </button>
              <button
                onClick={handleGuestContinue}
                className="mt-4 w-full py-3 text-sm font-medium text-text-secondary hover:text-text-primary transition-colors"
              >
                Continue as guest
              </button>
            </div>
          </div>
        ) : showWelcomeWithSavedAddressButton ? (
          <div className="px-4 py-6">
            {/* Welcome Message for Authenticated Users */}
            <div className="flex items-center gap-3 p-4 bg-linear-to-r from-brand-primary/10 to-brand-primary/5 rounded-xl border border-brand-primary/20 mb-4">
              <div className="size-12 flex items-center justify-center rounded-full bg-brand-primary/10">
                <UserIcon className="size-6 text-brand-primary" />
              </div>
              <div className="flex-1">
                <p className="text-base font-semibold text-text-primary">
                  Hi {appUser?.firstName || 'there'}!
                </p>
                <p className="text-sm text-text-secondary">
                  Welcome back to your delivery options
                </p>
              </div>
            </div>

            {/* Saved Addresses Button */}
            {state.savedAddresses.length > 0 && (
              <button
                onClick={() => {
                  // Show saved addresses by scrolling or navigating
                  const savedAddressesSection = document.querySelector('[data-saved-addresses]')
                  if (savedAddressesSection) {
                    savedAddressesSection.scrollIntoView({ behavior: 'smooth' })
                  }
                }}
                className="
                  w-full h-13 px-6
                  bg-brand-primary text-white
                  rounded-xl font-semibold
                  hover:opacity-90
                  active:scale-[0.98]
                  transition-all duration-200
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary focus-visible:ring-offset-2
                  flex items-center justify-center gap-2
                "
              >
                <MapPinIcon className="size-5" />
                View Saved Addresses ({state.savedAddresses.length})
              </button>
            )}

            {/* Show saved addresses list below */}
            {state.savedAddresses.length > 0 && (
              <div className="mt-6" data-saved-addresses>
                <SavedAddresses
                  savedAddresses={state.savedAddresses}
                  recentAddresses={state.recentAddresses}
                  onSelect={(address) => {
                    navigate('/delivery-results', { state: { deliveryAddress: address } })
                  }}
                />
              </div>
            )}
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <SpinnerIcon className="size-6 text-text-tertiary" />
          </div>
        ) : showSearchPrompt ? (
          // Guest mode or auth with no saved - show helpful message
          <div className="px-4 py-6 text-center">
            <p className="text-sm/5 text-text-secondary mb-4">
              Start typing to search for your address
            </p>
            {/* Manual entry button commented out */}
            {/* <button
              onClick={() => setShowManualEntry(true)}
              className="text-sm/5 text-brand-primary font-medium hover:underline"
            >
              Or enter your address manually
            </button> */}
          </div>
        ) : null}
      </div>

      {/* Auth Loading Overlay */}
      <AuthLoadingOverlay isLoading={state.authLoading} />
    </div>
  )
}
